"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.fromLazy = exports.isTupleOf = exports.isArrayOf = exports.isPartialObjectOf = exports.isObjectOf = exports.isRecordOf = exports.isMapOf = exports.hasElementsOf = exports.hasElements = exports.hasFieldsOf = exports.hasFields = exports.matchesRegExp = exports.hasExactLength = exports.hasLength = exports.isInstanceOf = exports.isLiteral = exports.isSha1SumHexString = exports.isHexString = exports.isNaturalNumber = exports.isPositiveInteger = exports.isObject = exports.isEmail = exports.optional = exports.isArray = exports.isAny = exports.isUndefined = exports.isNull = exports.isString = exports.isNumber = exports.isBoolean = exports.satisfiesPredicate = exports.satisfiesCondition = exports.isAllOf = exports.isSomeOf = exports.compose = exports.Validator = exports.ValidationError = void 0;
const lodash_1 = __importDefault(require("lodash"));
const validator_1 = __importDefault(require("validator"));
class ValidationError extends Error {
    constructor(description, value, root = value, path = []) {
        super(`Validation of ${root} failed${path.length > 0 ? ` at ${JSON.stringify(path)} = ${value}` : ''}: ${description}`);
        this.description = description;
        this.value = value;
        this.root = root;
        this.path = path;
        const error = Error;
        if ('captureStackTrace' in error) {
            error.captureStackTrace(this, this.constructor);
        }
    }
}
exports.ValidationError = ValidationError;
function Validator(validate, strip = validate) {
    function check(value) {
        try {
            validate(value);
            return true;
        }
        catch (e) {
            return false;
        }
    }
    function which(other) {
        return compose(other, check);
    }
    function or(other) {
        return isSomeOf(check, other);
    }
    function and(other) {
        return isAllOf(check, other);
    }
    check.or = or;
    check.and = and;
    check.validate = validate;
    check.which = which;
    check.strip = strip;
    return check;
}
exports.Validator = Validator;
function compose(isB, isA) {
    function validate(value) {
        return isB.validate(isA.validate(value));
    }
    function strip(value) {
        return isB.strip(isA.strip(value));
    }
    return Validator(validate, strip);
}
exports.compose = compose;
function isSomeOf(...validators) {
    function apply(fs) {
        return value => {
            const messages = new Array(fs.length);
            for (let i = 0; i < fs.length; i++) {
                try {
                    return fs[i](value);
                }
                catch (e) {
                    if (e instanceof ValidationError) {
                        messages[i] = e.message;
                    }
                    else {
                        throw e;
                    }
                }
            }
            throw new ValidationError(messages.join(' nor '), value);
        };
    }
    const validate = apply(validators.map((v) => v.validate));
    const strip = apply(validators.map((v) => v.strip));
    return Validator(validate, strip);
}
exports.isSomeOf = isSomeOf;
function isAllOf(...validators) {
    function validate(value) {
        validators.forEach(v => v.validate(value));
        return value;
    }
    function strip(value) {
        return lodash_1.default.merge({}, ...validators.map(v => v.strip(value)));
    }
    return Validator(validate, strip);
}
exports.isAllOf = isAllOf;
function satisfiesCondition(predicate, describe = () => `does not satisfy ${predicate.toString()}`) {
    function validate(value) {
        if (!predicate(value)) {
            throw new ValidationError(describe(value), value);
        }
        return value;
    }
    return Validator(validate);
}
exports.satisfiesCondition = satisfiesCondition;
function satisfiesPredicate(predicate) {
    return satisfiesCondition(predicate, () => { var _a; return `does not satisfy ${(_a = predicate.name) !== null && _a !== void 0 ? _a : predicate.toString()})`; });
}
exports.satisfiesPredicate = satisfiesPredicate;
function isPrimiteiveType(type) {
    return satisfiesCondition(v => typeof v === type, v => `is ${typeof v} but expected ${type}`);
}
exports.isBoolean = isPrimiteiveType('boolean');
exports.isNumber = isPrimiteiveType('number');
exports.isString = isPrimiteiveType('string');
exports.isNull = satisfiesCondition(v => v === null, v => `${v} is not null`);
exports.isUndefined = isPrimiteiveType('undefined');
exports.isAny = satisfiesCondition(() => true, () => 'anything is any');
exports.isArray = satisfiesCondition(Array.isArray, v => `${v} is not an array`);
const optional = (isSomething) => isSomeOf(isSomething, exports.isUndefined, exports.isNull);
exports.optional = optional;
exports.isEmail = exports.isString.which(satisfiesCondition(s => validator_1.default.isEmail(s)));
exports.isObject = satisfiesCondition(v => v !== null && typeof v === 'object', v => `${v} is not an object`);
const isPositiveIntegerCondition = satisfiesCondition(n => Number.isInteger(n) && n >= 1, n => `expected positive integer, got ${n}`);
exports.isPositiveInteger = exports.isNumber.which(isPositiveIntegerCondition);
const isNaturalNumberCondition = satisfiesCondition(n => Number.isInteger(n) && n >= 0, n => `expected positive integer or zero, got ${n}`);
exports.isNaturalNumber = exports.isNumber.which(isNaturalNumberCondition);
exports.isHexString = exports.isString.which(satisfiesCondition(s => /^[a-f0-9]+$/i.test(s)));
exports.isSha1SumHexString = exports.isHexString.which(satisfiesCondition(s => lodash_1.default.size(s) === 40));
function isLiteral(...literals) {
    return satisfiesCondition(value => literals.includes(value), value => `${JSON.stringify(value)} is not in ${literals
        .map(l => JSON.stringify(l))
        .join(', ')}`);
}
exports.isLiteral = isLiteral;
function isInstanceOf(constructor) {
    return satisfiesCondition(value => value instanceof constructor, value => `${value} is not ${constructor.name}`);
}
exports.isInstanceOf = isInstanceOf;
function hasLength(minLength, maxLength = Number.MAX_SAFE_INTEGER) {
    return Validator(value => {
        if (value.length < minLength) {
            throw new ValidationError(`${value.length} is less than minimum ${minLength}`, value.length, value, ['length']);
        }
        if (value.length > maxLength) {
            throw new ValidationError(`${value.length} is greater than maximum ${maxLength}`, value.length, value, ['length']);
        }
        return value;
    });
}
exports.hasLength = hasLength;
function hasExactLength(length) {
    return Validator(value => {
        if (value.length === length) {
            return value;
        }
        throw new ValidationError(`length should be ${length}`, value.length, value, ['length']);
    });
}
exports.hasExactLength = hasExactLength;
function matchesRegExp(re) {
    return satisfiesCondition(value => re.test(value), () => `does not match ${re}`);
}
exports.matchesRegExp = matchesRegExp;
function validateChild(childValidator, parent, key) {
    try {
        childValidator.validate(parent[key]);
    }
    catch (e) {
        if (e instanceof ValidationError) {
            throw new ValidationError(e.description, e.value, parent, [
                key,
                ...e.path,
            ]);
        }
        throw e;
    }
}
function stripChild(childValidator, parent, key) {
    try {
        return childValidator.strip(parent[key]);
    }
    catch (e) {
        if (e instanceof ValidationError) {
            throw new ValidationError(e.description, e.value, parent, [
                key,
                ...e.path,
            ]);
        }
        throw e;
    }
}
function hasFields(fieldValidators) {
    const keys = Object.keys(fieldValidators);
    function validate(value) {
        for (const key of keys) {
            validateChild(fieldValidators[key], value, key);
        }
        return value;
    }
    function strip(value) {
        const result = {};
        for (const key of keys) {
            const fieldValue = stripChild(fieldValidators[key], value, key);
            if (fieldValue !== undefined) {
                result[key] = fieldValue;
            }
        }
        return result;
    }
    return Validator(validate, strip);
}
exports.hasFields = hasFields;
function hasFieldsOf(memberValidator) {
    function validate(value) {
        for (const key in value) {
            validateChild(memberValidator, value, key);
        }
        return value;
    }
    function strip(value) {
        const result = {};
        for (const key in value) {
            result[key] = stripChild(memberValidator, value, key);
        }
        return value;
    }
    return Validator(validate, strip);
}
exports.hasFieldsOf = hasFieldsOf;
function hasElements(validators) {
    function validate(array) {
        for (let i = 0; i < validators.length; i++) {
            validateChild(validators[i], array, i);
        }
        return array;
    }
    function strip(array) {
        const result = new Array(validators.length);
        for (let i = 0; i < validators.length; i++) {
            result.push(stripChild(validators[i], array, i));
        }
        return result;
    }
    return Validator(validate, strip);
}
exports.hasElements = hasElements;
function hasElementsOf(elementValidator) {
    function validate(value) {
        for (let i = 0; i < value.length; i++) {
            validateChild(elementValidator, value, i);
        }
        return value;
    }
    function strip(value) {
        return value.map((_, i) => stripChild(elementValidator, value, i));
    }
    return Validator(validate, strip);
}
exports.hasElementsOf = hasElementsOf;
function isMapOf(validator) {
    return exports.isObject.which(hasFieldsOf(validator));
}
exports.isMapOf = isMapOf;
function isRecordOf(validator) {
    return isMapOf(validator);
}
exports.isRecordOf = isRecordOf;
function isObjectOf(fieldValidators) {
    return exports.isObject.which(hasFields(fieldValidators));
}
exports.isObjectOf = isObjectOf;
function isPartialObjectOf(fieldValidators) {
    const mapToOptional = (fv) => {
        return Object.fromEntries(Object.entries(fv).map(([k, v]) => [k, (0, exports.optional)(v)]));
    };
    const partialFieldValidators = mapToOptional(fieldValidators);
    return exports.isObject.which(hasFields(partialFieldValidators));
}
exports.isPartialObjectOf = isPartialObjectOf;
function isArrayOf(validator) {
    return exports.isArray.which(hasElementsOf(validator));
}
exports.isArrayOf = isArrayOf;
function isTupleOf(validators) {
    return exports.isArray.which(hasElements(validators));
}
exports.isTupleOf = isTupleOf;
function fromLazy(make) {
    let memo = null;
    function validateLazily(value) {
        if (!memo) {
            memo = make();
        }
        return memo.validate(value);
    }
    function stripLazily(value) {
        if (!memo) {
            memo = make();
        }
        return memo.strip(value);
    }
    return Validator(validateLazily, stripLazily);
}
exports.fromLazy = fromLazy;
//# sourceMappingURL=validation.js.map